import type { SessionService } from '@efdigital/ll-addon-auth';
import {
  CANCELLED_KEYS,
  COMPLETED_KEYS,
  HISTORY_KEYS,
  NEEDS_ATTENTION_BY_USER_KEYS,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-status';
import type {
  CoachingConfig,
  CoachingPlan,
  CoachingSession,
} from '@efdigital/ll-addon-data';
import type { CoachCoacheePii } from '@efdigital/ll-cdk-coaching-sessions-api-types';
import SessionCard from '@efdigital/ll-xceed-v2/components/coaching/coach/session-card.gts';
import { queryCoachingSessionsByCoach } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-sessions';
import { type CoachingCoachModel } from '@efdigital/ll-xceed-v2/routes/coaching/coach';
import CoachService from '@efdigital/ll-xceed-v2/services/coach';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await, Request } from '@warp-drive/ember';
import { pageTitle } from 'ember-page-title';

interface CoachingCoachSessionsIndexSignature {
  Args: {
    model: CoachingCoachModel;
    controller: unknown;
  };
}

async function processCoachingSessions(
  sessions: CoachingSession[],
  coachService: CoachService
) {
  const output: {
    session: CoachingSession;
    coachingPlan: CoachingPlan;
    coachingConfig: CoachingConfig;
    coachee?: CoachCoacheePii;
  }[] = [];

  const coachees = await coachService.getCoachees();

  for (const session of sessions) {
    const coachingPlan = (await session.coachingPlan)!;
    const coaching = (await coachingPlan.coaching)!;
    const coachingConfig = (await coaching.coachingConfig)!;
    const coachee = coachees.find((coachee) => coachee.userId === session.user);

    output.push({
      session,
      coachingPlan,
      coachingConfig,
      coachee,
    });
  }

  return output;
}

export default class CoachingCoachSessionsIndex extends Component<CoachingCoachSessionsIndexSignature> {
  @service declare session: SessionService;
  @service declare store: Store;
  @service declare coach: CoachService;

  get queryAllHistorySessions() {
    return queryCoachingSessionsByCoach(this.session.cached.userId!, [
      ...HISTORY_KEYS,
      ...COMPLETED_KEYS,
      ...NEEDS_ATTENTION_BY_USER_KEYS,
      ...CANCELLED_KEYS,
    ]);
  }

  <template>
    {{pageTitle "Sessions"}}

    <Request @query={{this.queryAllHistorySessions}}>
      <:content as |content|>
        {{#if content.data.length}}
          <Await @promise={{processCoachingSessions content.data this.coach}}>
            <:success as |sessions|>
              <div class="container my-3">
                <div class="mb-4">
                  <h3 class="gud-eyebrow">History</h3>
                  {{#each sessions as |item|}}
                    <SessionCard
                      @session={{item.session}}
                      @coach={{@model.coach}}
                      @coachee={{item.coachee}}
                      @coachingConfig={{item.coachingConfig}}
                      class="mb-2"
                    />
                  {{/each}}
                </div>
              </div>
            </:success>
          </Await>
        {{/if}}
      </:content>
    </Request>
  </template>
}
